import { PAGE_REGISTRY, type PageRoute } from '@/site/pageRegistry';

/**
 * The site top bar's links that come from the page registry (SiteHeader, components/marketing).
 * Guides = every registry page except the home pages (/, /de, /fr) and the changelog, so a new
 * guide page shows up in the Guides menu by being registered. Each href is the canonical's path (always slashed: a
 * slashless page path 301s on Firebase Hosting).
 */
const NOT_GUIDES: ReadonlySet<PageRoute> = new Set<PageRoute>(['/', '/de', '/fr', '/whats-new']);

/** Words a nav label keeps capitalised: names (an all-caps word such as PDF keeps its case on its own). */
const NAMES = new Set(['Bluebeam', 'Revu', 'Mac', 'Windows', 'Linux', 'Apple', 'Pencil', 'iPad']);

/** A registry label (Title Case) in sentence case for the nav (copyRules: sentence case), names kept. */
export function sentenceCaseLabel(label: string): string {
    return label
        .split(' ')
        .map((word, i) => {
            if (i === 0 || NAMES.has(word) || (word.length > 1 && word === word.toUpperCase())) return word;
            return word.toLowerCase();
        })
        .join(' ');
}

/** The canonical path of a registry page (`/whats-new/`, `/de/`, `/`). */
export function pagePath(route: PageRoute): string {
    const entry = PAGE_REGISTRY.find((p) => p.route === route);
    if (!entry) throw new Error(`No page registry entry for route: ${route}`);
    return new URL(entry.canonical).pathname;
}

export interface SiteNavLink {
    route: PageRoute;
    href: string;
    label: string;
}

/** The Guides menu, in registry order. */
export const GUIDE_LINKS: readonly SiteNavLink[] = PAGE_REGISTRY
    .filter((p) => !NOT_GUIDES.has(p.route))
    .map((p) => ({ route: p.route, href: new URL(p.canonical).pathname, label: sentenceCaseLabel(p.label) }));
